import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.background};
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 1.2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize.h1};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ResultsContainer = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const ResultCard = styled(motion.div)`
  background-color: #fff;
  border-radius: 12px;
  padding: 0.5rem 0.8rem;
  margin-bottom: 0.5rem;
  box-shadow: 0 1px 4px rgba(44, 62, 80, 0.06);
  font-size: 0.98rem;
`;

const ResultTitle = styled.h3`
  font-size: 1rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.1rem;
`;

const ResultText = styled.p`
  font-size: 0.97rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 0.05rem;
  line-height: 1.3;
`;

const ProductSection = styled(motion.div)`
  background-color: ${props => props.theme.colors.backgroundAlt};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${props => props.theme.spacing.xl};
  margin-top: ${props => props.theme.spacing.xl};
`;

const ProductTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.h2};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.lg};
  text-align: center;
`;

const ProductImage = styled.img`
  width: 100%;
  max-width: 300px;
  margin: 0 auto ${props => props.theme.spacing.xl};
  display: block;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.typography.fontSize.body};
  color: ${props => props.theme.colors.textPrimary};

  &:before {
    content: '✓';
    color: ${props => props.theme.colors.secondary};
    margin-right: ${props => props.theme.spacing.md};
    font-weight: bold;
  }
`;

const PriceContainer = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const OriginalPrice = styled.span`
  font-size: ${props => props.theme.typography.fontSize.h4};
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: line-through;
  margin-right: ${props => props.theme.spacing.md};
`;

const DiscountPrice = styled.span`
  font-size: ${props => props.theme.typography.fontSize.h3};
  color: ${props => props.theme.colors.primary};
  font-weight: bold;
`;

const CTAButton = styled(motion.button)`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: ${props => props.theme.typography.fontSize.h4};
  font-weight: 600;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  display: block;
  box-shadow: ${props => props.theme.shadows.medium};
  transition: transform ${props => props.theme.transitions.default},
              background-color ${props => props.theme.transitions.default};

  &:hover {
    transform: translateY(-2px);
    background-color: ${props => props.theme.colors.secondary};
  }

  &:active {
    transform: translateY(0);
  }
`;

const Guarantee = styled.p`
  text-align: center;
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${props => props.theme.spacing.xl};
`;

const ChartWrapper = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 1.2rem 0.5rem 1.2rem 0.5rem;
  margin: 0.5rem auto 1.2rem auto;
  max-width: 350px;
  color: #2C3E50;
  position: relative;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
`;

const ChartHeadline = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #fff;
`;

const MilestoneBox = styled.div`
  background: #A18AFF;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  position: absolute;
  left: 60%;
  top: 38%;
  transform: translate(-50%, -100%);
  z-index: 2;
  box-shadow: 0 2px 8px rgba(161,138,255,0.15);
`;

const JetztLabel = styled.div`
  background: #A18AFF;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.2rem 0.8rem;
  position: absolute;
  left: 13%;
  top: 62%;
  transform: translate(-50%, 0);
  z-index: 2;
`;

const ChartDate = styled.div`
  color: #A18AFF;
  font-size: 0.95rem;
  font-weight: 500;
  position: absolute;
`;

const GraphContainer = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 2rem auto 2rem auto;
  background: #f9f7f5;
  border-radius: 12px;
  padding: 1.5rem 1rem;
  box-shadow: 0 2px 8px rgba(74, 111, 165, 0.07);
`;

const GraphTitle = styled.div`
  font-size: 1.1rem;
  color: #2C3E50;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
`;

const BarGraph = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const BarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BarLabel = styled.div`
  width: 120px;
  font-size: 0.98rem;
  color: #6C757D;
`;

const Bar = styled.div`
  height: 22px;
  border-radius: 8px;
  background: ${props => props.active ? '#4A6FA5' : '#E8B4B8'};
  width: ${props => props.width};
  transition: width 0.7s;
`;

const ConversionHeadline = styled.h2`
  font-size: 1.3rem;
  color: #4A6FA5;
  text-align: center;
  margin-bottom: 1.2rem;
  font-weight: 700;
`;

const MainImage = styled.img`
  width: 220px;
  height: 220px;
  object-fit: contain;
  border-radius: 12px;
  margin: 0 auto 1rem auto;
  display: block;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
`;

const Thumbnails = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.7rem;
  margin-bottom: 1.2rem;
`;

const Thumbnail = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#4A6FA5' : '#E9ECEF'};
  cursor: pointer;
  background: #fff;
`;

const ReviewsSection = styled.div`
  margin: 2rem auto 1.5rem auto;
  max-width: 400px;
  background: #f9f7f5;
  border-radius: 12px;
  padding: 1.2rem 1rem;
  box-shadow: 0 2px 8px rgba(74, 111, 165, 0.07);
`;

const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  color: #FFC107;
  margin-bottom: 0.5rem;
`;

const ReviewCarousel = styled.div`
  overflow: hidden;
  height: 48px;
  position: relative;
`;

const ReviewText = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  font-size: 1rem;
  color: #2C3E50;
  text-align: center;
  opacity: 0;
  transition: opacity 0.5s;
  &.active {
    opacity: 1;
    position: relative;
  }
`;

const WeiterButton = styled(CTAButton)`
  margin-top: 2rem;
`;

const timelineData = [
  { label: 'Start', with: 10, without: 10 },
  { label: '2 Wochen', with: 40, without: 12 },
  { label: '4 Wochen', with: 80, without: 15 },
  { label: '8 Wochen', with: 95, without: 18 },
];

const pieData = [
  { label: 'Feuchtigkeit', value: 65, color: '#4A6FA5' },
  { label: 'Elastizität', value: 55, color: '#E8B4B8' },
  { label: 'Strahlkraft', value: 70, color: '#28A745' },
];

const reviews = [
  {
    name: 'Sabine M.',
    text: 'Ich verwende Derma Renew Complex seit zwei Monaten und bin wirklich beeindruckt! Die Wirkung macht sich bemerkbar – meine Haut fühlt sich straffer und vitaler an. Besonders an meinem Dekolleté sehe ich eine positive Veränderung. Die Kapseln sind angenehm klein und leicht zu schlucken. Ich freue mich jeden Morgen darauf, etwas Gutes für meine Haut zu tun.',
  },
  {
    name: 'Petra S.',
    text: 'Derma Renew Complex hat mich überzeugt! Am Anfang war ich eher skeptisch, aber nach einigen Wochen konnte ich tatsächlich erste Veränderungen an meiner Haut beobachten. Was mir besonders gefällt, ist die natürliche Zusammensetzung der Inhaltsstoffe. Die Einnahme ist unkompliziert und lässt sich perfekt in meine Morgenroutine integrieren. Endlich eine Hautpflege von innen, die wirkt!',
  },
  {
    name: 'Claudia R.',
    text: 'Für mich eine tolle Entdeckung! Seit ich Derma Renew Complex nehme, fühlt sich meine Haut deutlich geschmeidiger an. Besonders an Hals und Armen sehe ich eine Verbesserung des Hautbildes. Die regelmäßige Einnahme ist durch die praktischen Kapseln sehr einfach. Meine Haut wirkt insgesamt frischer und gepflegter – ich bin sehr zufrieden mit den Ergebnissen!',
  },
];

const ResultsScreen = ({ answers, onWeiter }) => {
  const [reviewIdx, setReviewIdx] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => {
      setReviewIdx(idx => (idx + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Calculate results based on answers
  const skinType = calculateSkinType(answers);
  const riskLevel = 'Hohes Alterungsrisiko';
  const mainProblem = answers['question-2'] || 'Hautalterung';

  // 1. Progress chart for 12 weeks, two lines, smaller text, brand colors
  const weeks = Array.from({ length: 13 }, (_, i) => i);
  const dermaRenewData = [10, 18, 27, 38, 50, 58, 63, 68, 71, 73, 75, 76, 77];
  const ohneDermapurisData = Array(13).fill(12);
  const chartHeight = 120;
  const chartWidth = 320;
  const maxVal = 100;
  const getY = v => chartHeight - (v / maxVal) * chartHeight + 10;
  const getX = i => 20 + (i * (chartWidth - 40) / 12);

  return (
    <Container>
      <Header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Title>Ihre personalisierte Hautanalyse</Title>
      </Header>
      <ResultsContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {/* Timeline Chart */}
        <ChartWrapper>
          <div style={{ textAlign: 'center', color: '#4A6FA5', fontWeight: 700, fontSize: '1.08rem', marginBottom: 4 }}>Hautregeneration in %</div>
          <ChartHeadline style={{ fontSize: '1.05rem', marginBottom: 10 }}>
            Fortschritt Ihrer Hautentwicklung (12 Wochen)
          </ChartHeadline>
          <svg width={chartWidth} height={chartHeight + 40} style={{ display: 'block', margin: '0 auto' }}>
            {/* Grid lines */}
            {[0, 25, 50, 75, 100].map((v, idx) => (
              <g key={v}>
                <line x1={20} x2={chartWidth - 20} y1={getY(v)} y2={getY(v)} stroke="#E9ECEF" strokeWidth="1" />
                <text x={5} y={getY(v) + 4} fontSize="9" fill="#6C757D">{v}%</text>
              </g>
            ))}
            {/* Derma Renew line */}
            <polyline
              fill="none"
              stroke="#4A6FA5"
              strokeWidth="3.5"
              points={dermaRenewData.map((v, i) => `${getX(i)},${getY(v)}`).join(' ')}
            />
            {/* Ohne Dermapuris line */}
            <polyline
              fill="none"
              stroke="#E8B4B8"
              strokeWidth="3.5"
              strokeDasharray="4 4"
              points={ohneDermapurisData.map((v, i) => `${getX(i)},${getY(v)}`).join(' ')}
            />
            {/* Dots and week labels */}
            {weeks.map((w, i) => (
              <g key={i}>
                <circle cx={getX(i)} cy={getY(dermaRenewData[i])} r="4" fill="#4A6FA5" />
                <circle cx={getX(i)} cy={getY(ohneDermapurisData[i])} r="4" fill="#E8B4B8" />
                {i % 2 === 0 && (
                  <text x={getX(i)} y={chartHeight + 25} fontSize="9" fill="#6C757D" textAnchor="middle">{i}W</text>
                )}
              </g>
            ))}
          </svg>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, margin: '0.5rem 0 0.5rem 0', fontSize: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, background: '#4A6FA5', borderRadius: 3, marginRight: 4 }}></span>
              Mit Derma Renew
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ display: 'inline-block', width: 14, height: 14, background: '#E8B4B8', borderRadius: 3, marginRight: 4 }}></span>
              Ohne Derma Renew
            </div>
          </div>
        </ChartWrapper>
        {/* Pie Charts */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, margin: '1.5rem 0 2.5rem 0' }}>
          {pieData.map((pie, idx) => (
            <div key={idx} style={{ width: 70, height: 70, position: 'relative' }}>
              <svg width="70" height="70" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="16" fill="#E9ECEF" />
                <circle
                  cx="18" cy="18" r="16"
                  fill="none"
                  stroke={pie.color}
                  strokeWidth="4"
                  strokeDasharray={`${pie.value / 100 * 100} ${100 - pie.value / 100 * 100}`}
                  strokeDashoffset="25"
                />
                <text x="18" y="22" textAnchor="middle" fontSize="8" fill="#2C3E50">{pie.value}%</text>
              </svg>
              <div style={{ textAlign: 'center', fontSize: 11, color: '#6C757D', marginTop: 2 }}>{pie.label}</div>
            </div>
          ))}
        </div>
        <ResultCard>
          <ResultTitle>Ihr Hauttyp</ResultTitle>
          <ResultText>{skinType}</ResultText>
        </ResultCard>
        <ResultCard>
          <ResultTitle>Alterungsrisiko</ResultTitle>
          <ResultText>{riskLevel}</ResultText>
        </ResultCard>
        <ResultCard>
          <ResultTitle>Hauptproblem</ResultTitle>
          <ResultText>{mainProblem}</ResultText>
        </ResultCard>
        <WeiterButton
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onWeiter}
        >
          Weiter
        </WeiterButton>
        {/* Reviews Section - styled as screenshot, below main button */}
        <div style={{ maxWidth: 600, margin: '2rem auto 0 auto', width: '100%' }}>
          <div style={{ textAlign: 'center', color: '#6C757D', fontSize: 15, marginBottom: 8 }}>
            <span style={{ fontWeight: 600, color: '#2C3E50', fontSize: 17 }}>★ 4.83/5</span> aus 847 Bewertungen
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 18 }}>
            <span style={{ fontSize: 22, color: '#FFC107' }}>★</span>
            <span style={{ fontSize: 22, color: '#FFC107' }}>★</span>
            <span style={{ fontSize: 22, color: '#FFC107' }}>★</span>
            <span style={{ fontSize: 22, color: '#FFC107' }}>★</span>
            <span style={{ fontSize: 22, color: '#FFC107' }}>★</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginBottom: 18, color: '#2C3E50', fontWeight: 500 }}>
            <span style={{ fontSize: 18 }}>👥</span> Über 2.500 zufriedene Nutzer:innen
          </div>
          <div style={{ position: 'relative', height: 140, maxWidth: 600, margin: '0 auto' }}>
            {reviews.map((review, idx) => {
              // Generate initials
              const initials = review.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
              // Fake dates for demo
              const dates = ['15.3.2025', '2.2.2025', '28.1.2025'];
              return (
                <div key={idx} style={{
                  position: reviewIdx === idx ? 'relative' : 'absolute',
                  opacity: reviewIdx === idx ? 1 : 0,
                  transition: 'opacity 0.7s',
                  width: '100%',
                  left: 0,
                  top: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}>
                  <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 8px rgba(44,62,80,0.07)', padding: '1.2rem 1.2rem 1rem 1.2rem', minWidth: 260, maxWidth: 340, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#E8B4B8', color: '#fff', fontWeight: 700, fontSize: 17, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{initials}</div>
                      <div>
                        <span style={{ fontWeight: 700, color: '#2C3E50', fontSize: 15 }}>{review.name}</span>
                        <span style={{ marginLeft: 6, color: '#4A6FA5', fontSize: 15, fontWeight: 700 }}>G</span>
                        <span style={{ marginLeft: 6, color: '#28A745', fontWeight: 600, fontSize: 13, verticalAlign: 'middle' }}>Verifiziert</span>
                        <div style={{ display: 'flex', gap: 2, marginTop: 2 }}>
                          <span style={{ color: '#FFC107', fontSize: 15 }}>★</span>
                          <span style={{ color: '#FFC107', fontSize: 15 }}>★</span>
                          <span style={{ color: '#FFC107', fontSize: 15 }}>★</span>
                          <span style={{ color: '#FFC107', fontSize: 15 }}>★</span>
                          <span style={{ color: '#FFC107', fontSize: 15 }}>★</span>
                        </div>
                      </div>
                    </div>
                    <div style={{ color: '#2C3E50', fontSize: 15, marginBottom: 4 }}>{review.text}</div>
                    <div style={{ color: '#6C757D', fontSize: 13, marginTop: 2 }}>{dates[idx]}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ResultsContainer>
    </Container>
  );
};

// Helper functions to calculate results
const calculateSkinType = (answers) => {
  const age = answers['question-1'];
  const concern = answers['question-2'];
  
  if (age === '25-35 Jahre') {
    return 'Präventions-orientiert';
  } else if (age === '36-45 Jahre') {
    return 'Anti-Aging fokussiert';
  } else {
    return 'Reparatur-bedürftig';
  }
};

const calculateRiskLevel = (answers) => {
  const age = answers['question-1'];
  const lifestyle = answers['question-7'];
  const satisfaction = answers['question-4'];
  
  let riskScore = 0;
  
  // Age factor
  if (age === '25-35 Jahre') riskScore += 1;
  else if (age === '36-45 Jahre') riskScore += 2;
  else if (age === '46-55 Jahre') riskScore += 3;
  else if (age === '56-65 Jahre') riskScore += 4;
  else riskScore += 5;
  
  // Lifestyle factor
  if (lifestyle === 'Sehr aktiv und sportlich') riskScore -= 1;
  else if (lifestyle === 'Stressig und hektisch') riskScore += 2;
  
  // Satisfaction factor
  if (satisfaction === 'Sehr unzufrieden') riskScore += 2;
  else if (satisfaction === 'Unzufrieden') riskScore += 1;
  
  if (riskScore <= 3) return 'Niedriges Alterungsrisiko';
  else if (riskScore <= 6) return 'Moderates Alterungsrisiko';
  else return 'Hohes Alterungsrisiko';
};

export default ResultsScreen; 